import React, { ReactElement, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CancelRounded } from '@material-ui/icons';
import { SvgIcon } from '@material-ui/core';
import styled from '@emotion/styled';
import { isEmpty } from 'lodash';
import BasicModal from '../../modules/modals/BasicModal';
import HCaptcha from '../auth/Hcaptcha';
import JoinSkipButton from '../join/JoinSkipButton';
import { CloseBtn, DashlineSeparator, HCaptchaContainer, closeButton, bottomLinkStyle } from '../common/CommonStyle';
import Button from '../../atoms/buttons/Button';
import { getBackgroundTheme, getBorders } from '../../style';
import { Headline, NextButtonWrapper, buttonStyle, emailLabelStyle } from '../../screens/LoginStyle';
import { RootState } from '../../store';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import {
  requestChangeEmailOtpAction,
  requestOtpAction,
  requestPhoneOtpAction,
  setAuthLoading,
  setVerifyWallLoading,
} from '../../actions/authActions';
import { requestJoinOtpAction } from '../../actions/joinActions';

const WrapperStyle = styled('div')({
  display: 'block',
});

const ResendPasscodeModal = (props: any): ReactElement => {
  const { showResendPasscodeModal, handleClose, type, emailVerifyData, redirectStep = '', setResetOtp } = props;
  const [resendPasscodeCaptcha, setResendPasscodeCaptcha] = useState<string>('');
  const { cf_country, isLoginCaptchaEnabled, isJoinCaptchaEnabled } = useSelector(
    (state: RootState) => state.attributes
  );
  const settings = useSelector((state: RootState) => state.settings);
  const dispatch = useDispatch();
  const { isMobile } = useMediaBreakpoints();
  const privateTypes = ['privateChangeEmail', 'verify_email_wall'];
  const captchaCountry = privateTypes.includes(type) ? settings.cf_country : cf_country;
  const isResendOTP = true;

  const isCaptchaEnabled = (): boolean => {
    if (privateTypes.includes(type)) {
      return settings.is_member_hcaptcha_enabled;
    }

    return type === 'join' ? isJoinCaptchaEnabled : isLoginCaptchaEnabled;
  };

  const handleResendEmailOTP = () => {
    if (type === 'join') {
      dispatch(requestJoinOtpAction(emailVerifyData?.email, resendPasscodeCaptcha, isResendOTP));
    } else if (type === 'phone') {
      dispatch(requestPhoneOtpAction(emailVerifyData?.phoneNumber));
    } else if (type === 'privateChangeEmail') {
      dispatch(
        requestChangeEmailOtpAction(
          emailVerifyData?.email,
          type,
          redirectStep,
          resendPasscodeCaptcha,
          true,
          isResendOTP
        )
      );
    } else {
      dispatch(type === 'verify_email_wall' ? setVerifyWallLoading() : setAuthLoading());
      dispatch(requestOtpAction(emailVerifyData.email, type, resendPasscodeCaptcha, true, isResendOTP));
    }

    setResetOtp(true);
    handleClose();
  };

  return (
    <>
      <BasicModal
        isOpen={showResendPasscodeModal}
        hideClose={true}
        customStyle={{
          content: {
            borderRadius: '20px',
            padding: '30px 20px',
            maxWidth: isMobile ? 312 : 355,
            backgroundColor: getBackgroundTheme().fsModal,
            border: getBorders().DEFAULT,
            zIndex: 10002,
            top: 52,
          },
          overlay: {
            zIndex: 10002,
          },
        }}
        onClose={handleClose}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <CloseBtn data-test-id={`resend-passcode-close`} onClick={handleClose} style={{ top: 0, right: 0 }}>
          <SvgIcon component={CancelRounded} style={closeButton} />
        </CloseBtn>
        <WrapperStyle>
          <Headline
            style={{
              paddingTop: 30,
            }}
          >
            Didn’t get an email?{' '}
          </Headline>
          <DashlineSeparator />
          <p style={{ ...emailLabelStyle, fontWeight: 700, margin: 0 }}>Check your promotions/spam folder</p>
          <p style={{ ...emailLabelStyle, margin: '3px 0 10px 0' }}>
            Sometimes our emails are misidentified, so be sure to check your other folders. Please mark us as{' '}
            {`"Not spam"`}
            and move to Primary if you find your verification email in there.
          </p>
          <p style={{ ...emailLabelStyle, fontWeight: 700, margin: 0 }}>Check your email address</p>
          <p style={{ ...emailLabelStyle, margin: 0 }}>
            If you mistyped your email address or want to try a different email, you can change it by tapping {`"Edit"`}{' '}
            link.
          </p>
          <p style={{ ...emailLabelStyle, fontWeight: 700, margin: '10px 0 3px 0' }}>Resend your email</p>
          <p style={{ ...emailLabelStyle, margin: 0 }}>
            Double check your email to make sure it’s correct and we will resend the verification email to you.
          </p>
          <DashlineSeparator />
          {isCaptchaEnabled() && (
            <HCaptchaContainer>
              <HCaptcha storeCaptchaResponse={setResendPasscodeCaptcha} cfCountry={captchaCountry} />
            </HCaptchaContainer>
          )}
          <NextButtonWrapper>
            <Button
              customStyle={buttonStyle}
              buttonType="primaryRed"
              type="button"
              name="commit"
              onClick={handleResendEmailOTP}
              disabled={isCaptchaEnabled() && isEmpty(resendPasscodeCaptcha)}
            >
              Resend Email
            </Button>
            <JoinSkipButton label="Close" customStyle={bottomLinkStyle} onClick={handleClose} />
          </NextButtonWrapper>
        </WrapperStyle>
      </BasicModal>
    </>
  );
};

export default ResendPasscodeModal;
